/**
 * The ambient bed: soft wave-wash around the island (looped filtered noise
 * with two slow, detuned swell LFOs so it never repeats exactly) plus
 * sparse pentatonic chime plinks. Quiet on purpose — it exists so the
 * world never feels dead and the player can tell sound is on.
 */
import type { AudioEngine } from './engine';

const CHIME_DEGREES = [0, 2, 4, 7, 9];

export class AmbientBed {
  private started = false;
  private chimeTimer = 0;

  constructor(private readonly engine: AudioEngine) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    const ctx = this.engine.context;

    // ---- wave wash ----
    const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 480;
    lowpass.Q.value = 0.4;
    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 120; // no rumble

    const washGain = ctx.createGain();
    washGain.gain.value = 0.035;

    // two detuned swell LFOs — waves that never quite repeat
    for (const [rate, depth] of [
      [0.07, 0.02],
      [0.043, 0.014],
    ] as const) {
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = rate;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = depth;
      lfo.connect(lfoGain).connect(washGain.gain);
      lfo.start();
    }
    // the swell also brightens the water a little
    const cutoffLfo = ctx.createOscillator();
    cutoffLfo.type = 'sine';
    cutoffLfo.frequency.value = 0.05;
    const cutoffDepth = ctx.createGain();
    cutoffDepth.gain.value = 160;
    cutoffLfo.connect(cutoffDepth).connect(lowpass.frequency);
    cutoffLfo.start();

    noise.connect(highpass).connect(lowpass).connect(washGain).connect(this.engine.ambientBus);
    noise.start();

    this.scheduleChime();
  }

  /** Sparse, soft pentatonic plinks — mean interval ~9 s. */
  private scheduleChime(): void {
    this.chimeTimer = window.setTimeout(() => {
      const ctx = this.engine.context;
      if (ctx.state === 'running') {
        const degree = CHIME_DEGREES[(Math.random() * CHIME_DEGREES.length) | 0] as number;
        const octave = Math.random() < 0.5 ? 1 : 2;
        const hz = 220 * Math.pow(2, octave + degree / 12);
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = hz;
        const g = ctx.createGain();
        const t = ctx.currentTime;
        // eased in — a distant bell, not a struck one
        g.gain.setValueAtTime(0.0001, t);
        g.gain.linearRampToValueAtTime(0.032, t + 0.06);
        g.gain.exponentialRampToValueAtTime(0.0004, t + 3.2);
        osc.connect(g).connect(this.engine.ambientBus);
        osc.start(t);
        osc.stop(t + 3.4);
      }
      this.scheduleChime();
    }, 4500 + Math.random() * 9000);
  }

  stop(): void {
    window.clearTimeout(this.chimeTimer);
  }
}
